import {
  Component,
  ViewChild,
  Inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AppService } from 'src/app/app.service';
import { NetworkedAccountsSelectorComponent } from '../networked-accounts-selector/networked-accounts-selector.component';
import { CustomDataSource } from 'src/app/utils/custom-data-source';
import { tap } from 'rxjs/operators';
import { ApiDataSource } from '../../utils/api-data-source';
import { ApiRequestService } from '../api-request.service';
import { AccountModel } from '../../models/account.model';
import { UtilsService } from '../utils.service';

@Component({
  selector: 'app-global-accounts-selector',
  templateUrl: './global-accounts-selector.component.html',
  styleUrls: ['./global-accounts-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class GlobalAccountsSelectorComponent {
  displayedColumns = ['name'];
  dataSource: GlobalAccountsDataSource;

  /**
   * The accounts the user is already an approved member of, excluding their own personal account.
   *
   * These are shown above the search because the global search cannot offer them. It only returns accounts
   * with include_in_search_results set, which is a small minority: a member of any other account could
   * never pick their own employer, despite the field promising "accounts you have access to". Anyone
   * choosing an employer is far more likely to want an account they already belong to than an arbitrary
   * company they have to spell correctly.
   *
   * A personal account is excluded because it is not an employer. It exists so somebody awaiting approval
   * still belongs somewhere, and offering it here would let a user set themselves as their own employer.
   */
  myAccounts: { id: number; name: string }[] = [];

  loadingMyAccounts: boolean = false;

  /**
   * Which tab opens first: 0 is Your Accounts, 1 is the employer search.
   *
   * Set to the search once the memberships load and turn out to be empty, so a user with no company
   * accounts is not dropped onto a tab that has nothing in it. Same principle as the Employees layout:
   * land on the first tab that is actually useful, not on index 0 by default.
   */
  selectedTabIndex: number = 0;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public app: AppService,
    public api: ApiRequestService,
    public dialogRef: MatDialogRef<NetworkedAccountsSelectorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private utils: UtilsService
  ) {
    // Set multiple if it is not present and default it to false
    if (
      typeof this.data['multiple'] === 'undefined' ||
      typeof this.data['multiple'] !== 'boolean'
    ) {
      this.data['multiple'] = false;
    }

    if (this.data['multiple']) {
      this.displayedColumns.unshift('select');
    } else {
      this.displayedColumns.push('actions');
    }

    // Construct the datasource
    this.dataSource = new GlobalAccountsDataSource(
      this.app,
      this.api,
      this.data['multiple']
    );

    // Set a default selection if no selected values are passed through
    if (typeof this.data['selected'] === 'undefined') {
      this.data['selected'] = [];
    }

    // Loop and select the values in the data source
    for (let i = 0; i < this.data['selected'].length; i++) {
      this.dataSource.selection.select(this.data['selected'][i]);
    }

    // Set a default search of '' if no search term were provided.
    if (typeof this.data['search'] == 'undefined') {
      this.data['search'] = '';
    }
    // Set the provided search term.
    this.dataSource.search = this.data['search'];

    // Get the data
    this.dataSource.getData();

    // Load the user's own memberships, unless the caller is picking several accounts for some other
    // purpose. This section is about answering "which of my accounts is my employer", which only makes
    // sense for a single selection.
    if (!this.data['multiple']) {
      this.getMyAccounts();
    }
  }

  /**
   * Loads the accounts the user is an approved member of.
   *
   * v1/managed-accounts is already filtered to approved, non-deleted memberships server side, so a pending
   * or declined request cannot appear here and be chosen as an employer.
   *
   * @return {void}
   */
  private getMyAccounts(): void {
    this.loadingMyAccounts = true;
    this.api
      .makeRequest('get', 'v1/managed-accounts', {}, { limit: 100, offset: 0 })
      .then((response: any): void => {
        this.myAccounts = (response?.data || [])
          // Drop the user's own personal account: it is not an employer.
          .filter((account: any): boolean => !this.app.isPersonalAccount(account))
          .map((account: any) => ({ id: Number(account.id), name: account.name }));

        // Nothing to choose from, so open on the search instead.
        if (!this.myAccounts.length) {
          this.selectedTabIndex = 1;
        }
      })
      .catch((): void => {
        // The global search still works, so a failure here must not block the dialog. Send the user
        // straight to the search rather than leaving them on a tab that failed to load.
        this.myAccounts = [];
        this.selectedTabIndex = 1;
      })
      .finally((): boolean => (this.loadingMyAccounts = false));
  }

  ngAfterViewInit() {
    // The search table and its sort header now sit inside a tab. Tab bodies render eagerly here (plain
    // markup, not an ng-template matTabContent), so these view children do exist, but guard anyway: a
    // later change to lazy tab content would otherwise turn this into a null dereference on dialog open
    // rather than a visible mistake.
    if (!this.sort || !this.paginator) {
      return;
    }

    // Reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order_by = this.sort.active;
      this.dataSource.order = this.sort.direction;

      this.dataSource.getData();
    });

    // Subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onCanCreateNewAccount(): boolean {
    // Get the data.
    const data: AccountModel[] = this.dataSource.dataSubject.getValue();
    // Check the length and search term.
    return data.length == 0 && this.dataSource.search != '';
  }

  onSelectSingle(id: number) {
    if (typeof id == 'undefined' || id == null) {
      // Pass the search term.
      this.utils.showModal(
        'Confirmation Required.',
        'Are you sure you want to proceed with "' +
          this.dataSource.search +
          '" as the name of your company/employer?',
        () => {
          this.dialogRef.close(this.dataSource.search);
        }
      );
    } else {
      // Pass the selected id.
      this.dialogRef.close(id);
    }
  }

  onSelectMultiple() {
    this.dialogRef.close(this.dataSource.selection.selected);
  }

  onDeselectAllRecords() {
    this.dataSource.selection.clear();
  }

  onClose() {
    this.dialogRef.close();
  }
}

export class GlobalAccountsDataSource extends ApiDataSource {
  order_by = 'name';
  order = 'asc';

  // Debounce timer and delay for the entity name autocomplete.
  debounceTimer: any;
  debounceDelay: number = 300;

  getData(resetOffset: boolean = false) {
    // Do not perform a search if the search string is less than 3 characters.
    if (this.search.length < 3) {
      // Clear the previous results.
      this.clearResults();
      return;
    }
    // Check if the debounce timer is set.
    if (this.debounceTimer) {
      // Clear the debounce timer.
      clearTimeout(this.debounceTimer);
    }
    // Set the debounce timer.
    this.debounceTimer = setTimeout(() => {
      // Make the API request to get accounts.
      this.makeRequest('v2/global-account-search', resetOffset, {});
    }, this.debounceDelay);
  }
}
